/**
 * Pure data query functions extracted from DataContext.jsx.
 *
 * Each function takes explicit data arrays as parameters instead of
 * closing over React state. This makes them testable, reusable outside
 * of React, and free of side effects.
 */

// ─── AppFolio Status Constants ──────────────────────────────────────────────
// These are the real AppFolio statuses (13 total), replacing the collapsed
// open/closed/cancelled from the old Firestore XLS import.

export const ACTIVE_STATUSES = [
  'New', 'New by AppFolio', 'Estimate Requested', 'Estimated',
  'Assigned', 'Assigned by AppFolio', 'Scheduled', 'Waiting',
];

export const PENDING_CLOSE_STATUSES = ['Work Done', 'Ready to Bill'];

export const COMPLETED_STATUSES = ['Completed', 'Completed No Need To Bill'];

export const CANCELED_STATUSES = ['Canceled'];

// Cutoff for a "recent" Firestore window, N days before now. Date-only YYYY-MM-DD
// sorts <= both 'YYYY-MM-DD' (legacy) and ISO datetime (AppFolio), so it works in
// where('created_at', '>=', cutoff) against both stored formats. `now` injectable for tests.
export function recentWindowCutoff(days, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Check if a WO is actively in the tech's queue (needs action).
 * @param {Object} wo - Work order object
 * @returns {boolean}
 */
export function isActiveWO(wo) {
  return ACTIVE_STATUSES.includes(wo?.status);
}

/**
 * Check if a WO is pending close (work done, awaiting billing/completion).
 * @param {Object} wo - Work order object
 * @returns {boolean}
 */
export function isPendingCloseWO(wo) {
  return PENDING_CLOSE_STATUSES.includes(wo?.status);
}

/**
 * Check if a WO is completed.
 * @param {Object} wo - Work order object
 * @returns {boolean}
 */
export function isCompletedWO(wo) {
  return COMPLETED_STATUSES.includes(wo?.status);
}

/**
 * Check if a WO is canceled.
 * @param {Object} wo - Work order object
 * @returns {boolean}
 */
export function isCanceledWO(wo) {
  return CANCELED_STATUSES.includes(wo?.status);
}

/**
 * Get the status category for a given status string.
 * @param {string} status - AppFolio status value
 * @returns {'active'|'pending_close'|'completed'|'canceled'|'unknown'}
 */
export function getStatusCategory(status) {
  if (ACTIVE_STATUSES.includes(status)) return 'active';
  if (PENDING_CLOSE_STATUSES.includes(status)) return 'pending_close';
  if (COMPLETED_STATUSES.includes(status)) return 'completed';
  if (CANCELED_STATUSES.includes(status)) return 'canceled';
  return 'unknown';
}

/**
 * Get work orders for a specific unit.
 * @param {Array} workOrders - All work orders
 * @param {string} unitId - The canonical unit identifier
 * @param {number} [limit=20] - Max results to return
 * @returns {Array} Matching work orders sorted by createdDate descending
 */
export function getWOsForUnit(workOrders, unitId, limit = 20) {
  if (!unitId) return [];

  return workOrders
    .filter(wo => wo.unitId === unitId)
    .sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0))
    .slice(0, limit);
}

/**
 * Match a work order to its unit doc by canonical ID only.
 * NEVER fuzzy-match on unit name: single-family WOs have an empty unit name,
 * and "anyString".includes("") === true, which would false-match the first unit
 * in the list onto every no-unit WO. Single-family homes legitimately have no
 * unit doc — callers should fall back to wo.property for the display label.
 * @param {Array} units - All unit docs
 * @param {Object} wo - Work order (needs unitId)
 * @returns {Object|null} Matching unit doc, or null
 */
export function findUnitForWO(units, wo) {
  if (!wo?.unitId || !Array.isArray(units)) return null;
  return units.find(u => u.id === wo.unitId || u.unitId === wo.unitId) || null;
}

/**
 * Get work orders for a specific property, optionally excluding a unit.
 * @param {Array} workOrders - All work orders
 * @param {string} propertyId - The canonical property identifier
 * @param {string|null} [excludeUnitId=null] - Unit to exclude (avoids duplicating unit history)
 * @param {number} [limit=50] - Max results to return
 * @returns {Array} Matching work orders sorted by createdDate descending
 */
export function getWOsForProperty(workOrders, propertyId, excludeUnitId = null, limit = 50) {
  if (!propertyId) return [];

  return workOrders
    .filter(wo => {
      const propertyMatch = wo.propertyId === propertyId;
      const notExcluded = !excludeUnitId || wo.unitId !== excludeUnitId;
      return propertyMatch && notExcluded;
    })
    .sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0))
    .slice(0, limit);
}

/**
 * Get labor entries for a work order (matches by woId OR woNumber).
 * @param {Array} laborEntries - All labor entries
 * @param {string} woId - Work order identifier
 * @returns {Array} Matching labor entries
 */
export function getLaborForWO(laborEntries, woId) {
  return laborEntries.filter(entry =>
    entry.woId === woId || entry.woNumber === woId
  );
}

/**
 * Get parts usage for a work order (matches by woId OR woNumber).
 * @param {Array} partsUsage - All parts usage records
 * @param {string} woId - Work order identifier
 * @returns {Array} Matching parts usage records
 */
export function getPartsForWO(partsUsage, woId) {
  return partsUsage.filter(part =>
    part.woId === woId || part.woNumber === woId
  );
}

/**
 * Get open work orders assigned to a technician.
 * @param {Array} workOrders - All work orders
 * @param {string} techName - Technician name (case-insensitive includes match)
 * @returns {Array} Open work orders for the tech
 */
export function getOpenWOsForTech(workOrders, techName) {
  if (!techName) return [];

  return workOrders.filter(wo =>
    wo.tech?.toLowerCase().includes(techName.toLowerCase()) &&
    isActiveWO(wo)
  );
}

/**
 * Get cards matched to a technician by name (bidirectional includes).
 * @param {Array} cards - All cards
 * @param {string} techName - Technician name
 * @returns {Array} Cards matching the tech name
 */
export function getCardsForTech(cards, techName) {
  if (!techName) return [];

  const nameLower = techName.toLowerCase();
  return cards.filter(c => {
    const holder = (c.holderName || '').toLowerCase();
    const cardName = (c.cardName || '').toLowerCase();
    return holder.includes(nameLower) || nameLower.includes(holder) ||
           cardName.includes(nameLower) || nameLower.includes(cardName);
  });
}

/**
 * Get cards for a user - checks explicit assignedCards first, falls back to
 * displayName-based inference.
 * @param {Array} cards - All cards
 * @param {Array} users - All users (used for userId lookup)
 * @param {Object} targetUser - The user object (must have assignedCards and/or displayName)
 * @returns {Array} Cards for the user
 */
export function getCardsForUser(cards, users, targetUser) {
  if (!targetUser) return [];

  // Step 1: Check for explicit card assignments
  const assignedCards = targetUser.assignedCards || [];
  if (assignedCards.length > 0) {
    return cards.filter(c => assignedCards.includes(c.cardLast4));
  }

  // Step 2: Fallback to inference based on displayName
  const displayName = targetUser.displayName || '';
  if (!displayName) return [];

  const nameLower = displayName.toLowerCase();
  const firstName = nameLower.split(' ')[0];

  return cards.filter(c => {
    const holder = (c.holderName || '').toLowerCase();
    const cardName = (c.cardName || '').toLowerCase();

    return holder.includes(firstName) || firstName.includes(holder) ||
           cardName.includes(firstName) || firstName.includes(cardName) ||
           holder.includes(nameLower) || nameLower.includes(holder) ||
           cardName.includes(nameLower) || nameLower.includes(cardName);
  });
}

/**
 * Get cards that are not assigned to any user and have active status.
 * @param {Array} cards - All cards
 * @param {Array} users - All users
 * @returns {Array} Available (unassigned, active) cards
 */
export function getAvailableCards(cards, users) {
  const assignedCardNumbers = new Set();
  users.forEach(u => {
    (u.assignedCards || []).forEach(cardLast4 => {
      assignedCardNumbers.add(cardLast4);
    });
  });

  return cards.filter(c =>
    !assignedCardNumbers.has(c.cardLast4) &&
    c.status === 'active'
  );
}

/**
 * Resolve the current technician from auth user context.
 * Priority: POV override -> email match -> displayName includes -> first tech fallback.
 * @param {Array} technicians - All technicians
 * @param {Object|null} user - The authenticated user (needs email, displayName)
 * @param {string|null} povTechId - Admin POV override tech ID
 * @returns {Object|null} The resolved technician, or null if no user
 */
export function getCurrentTech(technicians, user, povTechId) {
  if (!user) return null;

  // Admin POV override - highest priority
  if (povTechId) {
    const povTech = technicians.find(t => t.id === povTechId);
    if (povTech) {
      return { ...povTech, _isPovOverride: true };
    }
  }

  // Try to match by email
  const matched = technicians.find(t =>
    t.email?.toLowerCase() === user.email?.toLowerCase()
  );
  if (matched) return matched;

  // Try to match by name from display name
  const nameMatch = technicians.find(t =>
    user.displayName?.toLowerCase().includes(t.name?.toLowerCase())
  );
  if (nameMatch) return nameMatch;

  // No match — the signed-in account is not a technician (e.g. an admin).
  // Return null rather than a fallback tech: attributing an admin's action to a
  // real tech (technicians[0]) is data-integrity poison. Callers handle null.
  return null;
}

/**
 * Format a full tech name as "FirstName L." for compact display.
 * @param {string} fullName - Full name like "Bijan Doyle"
 * @returns {string} Formatted name like "Bijan D."
 */
export function formatTechNameShort(fullName) {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

/**
 * Get recent WOs a tech has logged labor on within the last N days.
 * @param {Array} workOrders - All work orders
 * @param {Array} laborEntries - All labor entries
 * @param {string} techName - Technician name (case-insensitive exact match on labor.techName)
 * @param {number} [days=14] - Look-back window in days
 * @returns {Array} Unique WOs sorted by most recent labor date descending
 */
export function getRecentLaborWOsForTech(workOrders, laborEntries, techName, days = 14) {
  if (!techName) return [];

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);

  const nameLower = techName.toLowerCase();

  // Find labor entries for this tech within the date window
  const recentLabor = laborEntries.filter(entry => {
    if (entry.techName?.toLowerCase() !== nameLower) return false;
    const entryDate = new Date(entry.date || entry.createdDate || 0);
    return entryDate >= cutoff;
  });

  // Group by WO and find the most recent labor date per WO
  const woMap = new Map();
  for (const entry of recentLabor) {
    const woKey = entry.woId || entry.woNumber;
    if (!woKey) continue;
    const entryDate = new Date(entry.date || entry.createdDate || 0);
    const existing = woMap.get(woKey);
    if (!existing || entryDate > existing) {
      woMap.set(woKey, entryDate);
    }
  }

  // Match to actual WO objects and sort by most recent labor date
  const results = [];
  for (const [woKey, lastLaborDate] of woMap) {
    const wo = workOrders.find(w => w.id === woKey || w.woNumber === woKey);
    if (wo) {
      results.push({ ...wo, _lastLaborDate: lastLaborDate });
    }
  }

  results.sort((a, b) => b._lastLaborDate - a._lastLaborDate);
  return results;
}

/**
 * Compute leaderboard data for all technicians with corrected metrics.
 *
 * Fixes vs. the old inline computation:
 * - Completed WOs: filters by `completedOn` date (not createdDate)
 * - Hours: only labor entries from the current month
 * - Receipts: single count of unmatched bank charges
 * - Inventory: total inventory items count for this tech
 * - Consults: matches tech → user → uid → consult_sessions docs
 * - Name: formatted as "FirstName L."
 *
 * @param {Object} params
 * @param {Array} params.technicians
 * @param {Array} params.workOrders
 * @param {Array} params.laborEntries
 * @param {Array} params.satisfactionScores
 * @param {Array} params.partsUsage
 * @param {Array} params.consultSessions
 * @param {Array} params.bankCharges
 * @param {Array} params.receipts
 * @param {Array} params.users
 * @param {Function} params.getCardsForTech - (techName) => cards[]
 * @returns {Array} Sorted leaderboard rows
 */
export function computeLeaderboardData({
  technicians, workOrders, laborEntries, satisfactionScores,
  partsUsage, consultSessions, bankCharges, receipts,
  users, getCardsForTech, lookbackDays = 30,
}) {
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - lookbackDays);

  // Helper: is a date string/timestamp within the lookback window?
  const isWithinWindow = (dateVal) => {
    if (!dateVal) return false;
    // Handle Firestore Timestamps (have .toDate method)
    const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
    return d >= cutoff && d <= now;
  };

  // Pre-compute matched charge IDs once
  const matchedChargeIds = new Set(
    receipts.filter(r => r.bankChargeId).map(r => r.bankChargeId)
  );

  return technicians.map(tech => {
    const techNameLower = tech.name?.toLowerCase();

    // WOs assigned to this tech
    const techWOs = workOrders.filter(wo =>
      wo.tech?.toLowerCase().includes(techNameLower)
    );

    // Done WOs in last 30d — includes pending close (Work Done, Ready to Bill)
    // and fully completed statuses (tech's work is finished)
    const completedWOs = techWOs.filter(wo => {
      if (!isCompletedWO(wo) && !isPendingCloseWO(wo)) return false;
      const dateStr = wo.completedOn || wo.completedDate || wo.updatedAt;
      return isWithinWindow(dateStr);
    });

    // Set of recently-completed WO IDs (for parts filtering)
    const recentWoIds = new Set(completedWOs.map(wo => wo.id));
    const recentWoNumbers = new Set(completedWOs.map(wo => wo.woNumber).filter(Boolean));

    // Labor entries for this tech — last 30d
    const techLabor = laborEntries.filter(entry => {
      if (entry.techName?.toLowerCase() !== techNameLower) return false;
      return isWithinWindow(entry.date || entry.createdDate);
    });
    const totalHours = techLabor.reduce((sum, entry) => sum + (entry.hours || 0), 0);

    // Satisfaction scores — last 30d (if date available), else include all
    const techScores = satisfactionScores.filter(s => {
      if (!s.techName?.toLowerCase().includes(techNameLower)) return false;
      const scoreDate = s.date || s.dateReceived;
      if (scoreDate) return isWithinWindow(scoreDate);
      return true; // no date field → include (Firestore fallback)
    });
    const avgSatisfaction = techScores.length > 0
      ? techScores.reduce((sum, s) => sum + (s.score || 0), 0) / techScores.length
      : 0;

    // Inventory: parts for this tech's recently-completed WOs
    const inventoryCount = partsUsage.filter(p =>
      recentWoIds.has(p.woNumber) || recentWoNumbers.has(p.woNumber) ||
      recentWoIds.has(p.woId) || recentWoNumbers.has(p.woId)
    ).length;

    // Receipts: unmatched bank charges in last 30d
    const techCards = getCardsForTech(tech.name);
    const techCardNumbers = new Set(techCards.map(c => c.cardLast4));
    const techBankCharges = bankCharges.filter(charge => {
      if (!techCardNumbers.has(charge.cardLast4)) return false;
      const chargeDate = charge.transactionDate || charge.date;
      if (chargeDate) return isWithinWindow(chargeDate);
      return true; // no date → include (don't silently drop)
    });
    const unmatchedReceipts = techBankCharges.filter(charge =>
      !matchedChargeIds.has(charge.id)
    ).length;

    // Consults: match by techName (primary) or techId via user uid (fallback)
    const techUser = users.find(u =>
      u.displayName?.toLowerCase() === techNameLower ||
      u.email?.toLowerCase() === tech.email?.toLowerCase()
    );
    const techConsults = consultSessions.filter(c => {
      // Match by techName if available (works for POV/tunneled sessions)
      const nameMatch = c.techName?.toLowerCase() === techNameLower;
      // Fallback: match by techId → user uid
      const uidMatch = techUser && c.techId === techUser.uid;
      if (!nameMatch && !uidMatch) return false;
      const sessionDate = c.createdAt;
      if (sessionDate) return isWithinWindow(sessionDate);
      return true; // no date → include
    }).length;

    // Location status (uses ALL labor entries, not just lookback window)
    const location = getTechLocationStatus(tech.name, laborEntries);

    return {
      name: formatTechNameShort(tech.name),
      fullName: tech.name,
      wos: completedWOs.length,
      completedWOs,
      hours: Math.round(totalHours * 10) / 10,
      unmatchedReceipts,
      satisfaction: Math.round(avgSatisfaction * 10) / 10,
      inventoryCount,
      consults: techConsults,
      location,
    };
  })
  // Filter: must average at least 10 hrs/week to appear on leaderboard
  .filter(tech => {
    const weeks = lookbackDays / 7;
    const weeklyAvg = tech.hours / weeks;
    return weeklyAvg >= 10;
  })
  .sort((a, b) => b.wos - a.wos);
}

// ─── Tech Location Status ──────────────────────────────────────────────────

/**
 * Determine a tech's current location status based on labor entries.
 *
 * @param {string} techName - Full tech name (case-insensitive match)
 * @param {Array} laborEntries - All normalized labor entries
 * @returns {{ status: 'clocked-in'|'recent'|'offline', propertyName: string, ago: string }}
 *   - clocked-in: has a labor entry with startTime but no endTime (green)
 *   - recent: clocked out within the last hour and no new clock-in (grey)
 *   - offline: last activity was >1 hour ago (muted, shows time ago)
 */
export function getTechLocationStatus(techName, laborEntries) {
  const techNameLower = techName?.toLowerCase();
  if (!techNameLower) return { status: 'offline', propertyName: '', ago: 'Unknown' };

  // Get all labor entries for this tech, sorted newest first by startTime then date
  const techEntries = laborEntries
    .filter(e => e.techName?.toLowerCase() === techNameLower)
    .filter(e => e.startTime || e.date) // must have some time reference
    .sort((a, b) => {
      const ta = a.startTime ? new Date(a.startTime) : new Date(a.date);
      const tb = b.startTime ? new Date(b.startTime) : new Date(b.date);
      return tb - ta;
    });

  if (techEntries.length === 0) {
    return { status: 'offline', propertyName: '', ago: 'No labor entries' };
  }

  const latest = techEntries[0];
  const now = new Date();

  // Clocked in: has startTime but no endTime
  if (latest.startTime && !latest.endTime) {
    return {
      status: 'clocked-in',
      propertyName: latest.propertyName || '',
      ago: '',
    };
  }

  // Has an endTime — calculate how long ago they clocked out
  const endRef = latest.endTime
    ? new Date(latest.endTime)
    : latest.startTime
      ? new Date(latest.startTime)
      : new Date(latest.date);

  const diffMs = now - endRef;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Recent: clocked out within the last hour
  if (diffMs <= 3600000) {
    return {
      status: 'recent',
      propertyName: latest.propertyName || '',
      ago: diffMins <= 1 ? 'Just now' : `${diffMins}m ago`,
    };
  }

  // Offline: more than 1 hour ago
  let ago;
  if (diffHours < 24) {
    ago = `${diffHours}h ago`;
  } else if (diffDays === 1) {
    ago = 'Yesterday';
  } else {
    ago = `${diffDays}d ago`;
  }

  return { status: 'offline', propertyName: latest.propertyName || '', ago };
}

// ─── Firestore Normalizers (raw AppFolio → app schema) ─────────────────────
// These normalize raw AppFolio records stored in Firestore by the sync engine.
// Firestore stores ALL AppFolio fields with original names + _syncedAt/_syncType.

/**
 * Normalize a single raw AppFolio WO from Firestore to app schema.
 * @param {Object} raw - Raw Firestore doc data (AppFolio field names)
 * @returns {Object} Normalized work order
 */
export function normalizeFirestoreWO(raw) {
  return {
    id: String(raw.work_order_id || raw.firestoreId || ''),
    firestoreId: raw.firestoreId,
    woNumber: raw.work_order_number || '',
    status: raw.status || '',
    tech: raw.assigned_user || '',
    propertyId: String(raw.property_id || ''),
    property: raw.property_name || raw.property || '',
    unitId: String(raw.unit_id || ''),
    unit: raw.unit_name || '',
    description: raw.job_description || '',
    createdDate: raw.created_at || '',
    completedOn: raw.completed_on || '',
    workCompletedOn: raw.work_completed_on || '',
    resident: raw.primary_tenant || '',
    residentPhone: raw.primary_tenant_phone_number || '',
    workOrderType: raw.work_order_type || '',
    priority: raw.priority || '',
    _syncedAt: raw._syncedAt || null,
    _syncType: raw._syncType || null,
  };
}

/**
 * Normalize a single raw AppFolio labor entry from Firestore to app schema.
 * @param {Object} raw - Raw Firestore doc data
 * @returns {Object} Normalized labor entry
 */
export function normalizeFirestoreLabor(raw) {
  return {
    id: String(raw.labor_detail_id || raw.firestoreId || ''),
    firestoreId: raw.firestoreId,
    techName: raw.maintenance_tech || '',
    date: raw.date || '',
    hours: parseFloat(raw.worked_hours) || 0,
    billedHours: parseFloat(raw.hours) || 0,
    woNumber: raw.work_order_number || '',
    woId: String(raw.work_order_id || ''),
    propertyId: String(raw.property_id || ''),
    propertyName: raw.property_name || '',
    description: raw.description || '',
    startTime: raw.start_time || raw.timer_start || '',
    endTime: raw.end_time || raw.timer_stop || '',
    _syncedAt: raw._syncedAt || null,
  };
}

/**
 * Normalize a single raw AppFolio survey from Firestore to satisfaction score.
 * @param {Object} raw - Raw Firestore doc data
 * @returns {Object} Normalized satisfaction score
 */
export function normalizeFirestoreSurvey(raw) {
  // Find the satisfaction question and extract numeric score
  const satisfactionQ = (raw.questions || []).find(q =>
    q.question?.toLowerCase().includes('how satisfied')
  );
  const score = satisfactionQ ? (parseInt(satisfactionQ.answer) || 0) : 0;

  return {
    id: String(raw.survey_id || raw.firestoreId || ''),
    firestoreId: raw.firestoreId,
    techName: raw.assigned_user || '',
    score,
    date: raw.date_received || raw.date_sent || '',
    woId: String(raw.work_order_id || ''),
    woNumber: raw.work_order_number || '',
    feedback: (raw.questions || []).find(q =>
      q.question?.toLowerCase().includes('additional feedback')
    )?.answer || '',
    _syncedAt: raw._syncedAt || null,
  };
}

/**
 * Normalize a single raw AppFolio property from Firestore to app schema.
 * @param {Object} raw - Raw Firestore doc data
 * @returns {Object} Normalized property
 */
export function normalizeFirestoreProperty(raw) {
  return {
    id: String(raw.property_id || raw.firestoreId || ''),
    firestoreId: raw.firestoreId,
    name: raw.property_name || raw.name || '',
    address: raw.property_street || raw.address || '',
    city: raw.property_city || '',
    state: raw.property_state || '',
    zip: raw.property_zip || '',
    propertyType: raw.property_type || '',
    lat: raw.lat || null,
    lng: raw.lng || null,
    _syncedAt: raw._syncedAt || null,
  };
}

/**
 * Normalize a single raw AppFolio unit from Firestore to app schema.
 * @param {Object} raw - Raw Firestore doc data
 * @returns {Object} Normalized unit
 */
export function normalizeFirestoreUnit(raw) {
  return {
    id: String(raw.unit_id || raw.firestoreId || ''),
    firestoreId: raw.firestoreId,
    name: raw.unit_name || raw.name || '',
    propertyId: String(raw.property_id || ''),
    propertyName: raw.property_name || '',
    address: raw.unit_street || raw.address || '',
    unitType: raw.unit_type || '',
    visibility: raw.visibility || '',
    tags: raw.unit_tags || raw.tags || '',
    _syncedAt: raw._syncedAt || null,
  };
}

/**
 * Check whether a comma-separated AppFolio tag string contains an exact tag.
 * Exact-token, case-insensitive — "HGRX" does NOT match "HGR".
 * Mirrors the server-side Python tags_contain() semantics.
 * @param {string} tags - Comma-separated tag string (e.g. "Early Renewal, HGR")
 * @param {string} target - Tag to look for (e.g. "HGR")
 * @returns {boolean}
 */
export function unitHasTag(tags, target) {
  return String(tags || '')
    .split(',')
    .map(t => t.trim().toLowerCase())
    .includes(String(target).toLowerCase());
}

// ─── AppFolio Data Normalizers (API response → app schema) ─────────────────
// These transform raw AppFolio API responses (arrays) into the app's schema.
// Used by loadFromAppFolio (direct API calls) — kept for backward compat.

/**
 * Normalize AppFolio work order records to app schema.
 * @param {Array} appfolioWOs - Raw records from work_order report
 * @returns {Array} Normalized work orders
 */
export function normalizeAppFolioWorkOrder(appfolioWOs) {
  return appfolioWOs.map(wo => ({
    id: String(wo.work_order_id || ''),
    woNumber: wo.work_order_number || '',
    status: wo.status || '',
    tech: wo.assigned_user || '',
    propertyId: String(wo.property_id || ''),
    property: wo.property_name || wo.property || '',
    unitId: String(wo.unit_id || ''),
    unit: wo.unit_name || '',
    description: wo.job_description || '',
    createdDate: wo.created_at || '',
    completedOn: wo.completed_on || '',
    workCompletedOn: wo.work_completed_on || '',
    resident: wo.primary_tenant || '',
    residentPhone: wo.primary_tenant_phone_number || '',
    workOrderType: wo.work_order_type || '',
    priority: wo.priority || '',
  }));
}

/**
 * Normalize AppFolio labor-summary records to labor entry shape.
 * @param {Array} appfolioLabor - Raw records from work_order_labor_summary report
 * @returns {Array} Normalized entries compatible with computeLeaderboardData
 */
export function normalizeAppFolioLabor(appfolioLabor) {
  return appfolioLabor.map(entry => ({
    id: String(entry.id || entry.labor_detail_id),
    techName: entry.maintenance_tech || '',
    date: entry.date || '',
    hours: parseFloat(entry.worked_hours) || 0,
    billedHours: parseFloat(entry.hours) || 0,
    woNumber: entry.work_order_number || '',
    woId: String(entry.work_order_id || ''),
    propertyId: String(entry.property_id || ''),
    propertyName: entry.property_name || '',
    description: entry.description || '',
    startTime: entry.start_time || entry.timer_start || '',
    endTime: entry.end_time || entry.timer_stop || '',
  }));
}

/**
 * Normalize AppFolio survey records to satisfaction score shape.
 * Extracts star rating from the questions array.
 * @param {Array} appfolioSurveys - Raw records from surveys_summary report
 * @returns {Array} Normalized scores compatible with computeLeaderboardData
 */
export function normalizeAppFolioSurveys(appfolioSurveys) {
  return appfolioSurveys.map(survey => {
    // Find the satisfaction question and extract numeric score
    const satisfactionQ = (survey.questions || []).find(q =>
      q.question?.toLowerCase().includes('how satisfied')
    );
    const score = satisfactionQ ? (parseInt(satisfactionQ.answer) || 0) : 0;

    return {
      id: String(survey.id || survey.survey_id),
      techName: survey.assigned_user || '',
      score,
      date: survey.date_received || survey.date_sent || '',
      woId: String(survey.work_order_id || ''),
      woNumber: survey.work_order_number || '',
      feedback: (survey.questions || []).find(q =>
        q.question?.toLowerCase().includes('additional feedback')
      )?.answer || '',
    };
  });
}
